import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Eye, ChevronRight, HelpCircle, TrendingUp, ArrowUp, ArrowDown, CarFront, Activity } from 'lucide-react';
import {
  getScoreColor, PLATFORM_CONFIG,
  VISIBILITY_LABELS as L, VITRINE_SEGMENT_COLORS,
} from '../../constants/visibility';

/* ── Info Tooltip ──────────────────────────────────────── */
const InfoTooltip = ({ text }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="relative inline-flex">
      <button
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        onClick={() => setShow(!show)}
        className="text-muted hover:text-secondary transition-colors"
      >
        <HelpCircle className="h-3.5 w-3.5" />
      </button>
      {show && (
        <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 w-64 p-3
          bg-dark-surface border border-dark-border rounded-lg shadow-lg z-50">
          <p className="text-xs text-secondary leading-relaxed">{text}</p>
        </div>
      )}
    </div>
  );
};

/* ── Score Circle ────────────────────────────────────────── */
/* segments: [{ value (0-100), color (hex) }] — multi-color donut mode */
/* children: optional custom center content (replaces default number) */
const ScoreCircle = ({ value, color, segments, size: sizeProp, strokeWidth: swProp, children }) => {
  const r = sizeProp ? (sizeProp / 2 - (swProp || 5)) : 40;
  const sw = swProp || 5;
  const size = (r + sw) * 2;
  const circ = 2 * Math.PI * r;
  const c = r + sw; // center

  if (segments && segments.length > 0) {
    const total = segments.reduce((s, seg) => s + seg.value, 0);
    let consumed = 0;
    return (
      <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <circle cx={c} cy={c} r={r} fill="none" stroke="currentColor" strokeWidth={sw} className="text-dark-surface" />
          {segments.map((seg, i) => {
            const segLen = (seg.value / 100) * circ;
            const offset = consumed;
            consumed += segLen;
            return (
              <circle
                key={i}
                cx={c} cy={c} r={r} fill="none"
                stroke={seg.color}
                strokeWidth={sw}
                strokeDasharray={`${segLen} ${circ - segLen}`}
                strokeDashoffset={-offset}
                transform={`rotate(-90 ${c} ${c})`}
              />
            );
          })}
        </svg>
        <span className="absolute inset-0 flex items-center justify-center">
          {children || <span className="text-lg font-bold text-primary">{Math.round(total)}</span>}
        </span>
      </div>
    );
  }

  const display = value !== null && value !== undefined;
  const pct = display ? value : 0;
  const colorClass = color || getScoreColor(value);

  return (
    <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
      <svg className="-rotate-90" width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={c} cy={c} r={r} fill="none" stroke="currentColor" strokeWidth={sw} className="text-dark-surface" />
        <circle
          cx={c} cy={c} r={r} fill="none"
          stroke={color || 'currentColor'} strokeWidth={sw}
          strokeDasharray={`${(pct / 100) * circ} ${circ}`}
          strokeLinecap="round"
          className={color ? undefined : colorClass}
        />
      </svg>
      <span className={`absolute inset-0 flex items-center justify-center text-lg font-bold ${color ? 'text-primary' : colorClass}`}>
        {display ? pct : '—'}
      </span>
    </div>
  );
};

/* ── Platform Row (clickable) ─────────────────────────────── */
const PlatformRow = ({ id, name, detail, published, total, onClick }) => {
  const cfg = PLATFORM_CONFIG[id] || { dotColor: 'bg-gray-400' };
  const Tag = onClick ? 'button' : 'div';

  return (
    <Tag
      onClick={onClick}
      className={`w-full flex items-center justify-between py-1.5 ${
        onClick ? 'group hover:bg-dark-hover/50 rounded-lg px-1.5 -mx-1.5 transition-colors cursor-pointer' : ''
      }`}
    >
      <div className="flex items-center gap-2">
        <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${cfg.dotColor}`} />
        <span className="text-xs text-primary font-medium">{name}</span>
      </div>
      <div className="flex items-center gap-1">
        <span className="text-xs font-medium text-primary">{detail}</span>
        {onClick && <ChevronRight className="h-3 w-3 text-muted opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />}
      </div>
    </Tag>
  );
};

/* ── Format compact € values ─────────────────────────────── */
const formatCompact = (v) => {
  if (!v) return '0€';
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1).replace('.0', '')}M€`;
  if (v >= 1_000) return `${Math.round(v / 1_000)}k€`;
  return `${v}€`;
};

/* ── Period Selector (pills row) ────────────────────────── */
const PERIODS = ['7', '14', '30', '60', '90'];
const PeriodSelector = ({ selected, onChange }) => (
  <div className="flex gap-1.5 overflow-x-auto -mx-1 px-1 scrollbar-hide">
    {PERIODS.map(p => (
      <button
        key={p}
        onClick={() => onChange(p)}
        className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
          selected === p
            ? 'bg-cyan-500/20 text-cyan-400 ring-1 ring-cyan-500/30'
            : 'bg-dark-hover text-muted hover:text-secondary'
        }`}
      >
        {p}{L.days}
      </button>
    ))}
  </div>
);

/* ── Evolution indicator ────────────────────────────────── */
const EvolutionBadge = ({ current, previous, invert = false }) => {
  if (current == null || previous == null) return null;
  const diff = current - previous;
  if (diff === 0) return null;
  const isPositive = invert ? diff < 0 : diff > 0;
  const color = isPositive ? 'text-green-400' : 'text-red-400';
  const Icon = diff > 0 ? ArrowUp : ArrowDown;
  const displayDiff = invert ? -diff : diff;
  const sign = displayDiff > 0 ? '+' : '';
  return (
    <span className={`inline-flex items-center gap-0.5 text-[11px] font-medium ${color}`}>
      <Icon className="h-3 w-3" />
      {sign}{Math.abs(diff) % 1 === 0 ? Math.abs(diff) : Math.abs(diff).toFixed(1)}
    </span>
  );
};

/* ── Stock Health Row ───────────────────────────────────── */
const StockHealthRow = ({ label, count, total, dotColor, barColor, onClick }) => {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-2.5 py-1.5 group hover:bg-dark-hover/50 rounded-lg px-1.5 -mx-1.5 transition-colors"
    >
      <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${dotColor}`} />
      <span className="text-xs text-secondary flex-shrink-0">{label}</span>
      <span className="text-xs font-medium text-primary w-6 text-right flex-shrink-0">{count}</span>
      <div className="flex-1 h-1.5 bg-dark-hover rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${barColor}`}
          style={{ width: `${Math.max(pct, count > 0 ? 4 : 0)}%` }}
        />
      </div>
      <ChevronRight className="h-3 w-3 text-muted opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
    </button>
  );
};

/* ── getDaysInStock helper ──────────────────────────────── */
const getDaysInStock = (createdAt) => {
  if (!createdAt) return 0;
  return Math.floor((new Date() - new Date(createdAt)) / (1000 * 60 * 60 * 24));
};

/* ── Performance Summary (natural language) ──────────────── */
const PerformanceSummary = ({ period, periodLabel }) => {
  if (!period) return null;
  const entries = period.entries?.count ?? 0;
  const sales = period.sales?.count ?? 0;
  const rotation = period.sales?.avg_days;
  const prevRotation = period.prev?.sales?.avg_days;
  const net = entries - sales;

  if (entries === 0 && sales === 0) return null;

  // Stock flow sentence
  let flowPart;
  if (net > 0) {
    flowPart = <><span className="font-bold text-primary">{entries} entrée{entries > 1 ? 's' : ''}</span> et <span className="font-bold text-primary">{sales} vente{sales > 1 ? 's' : ''}</span> (<span className="text-green-400 font-bold">+{net}</span>)</>;
  } else if (net < 0) {
    flowPart = <><span className="font-bold text-primary">{entries} entrée{entries > 1 ? 's' : ''}</span> et <span className="font-bold text-primary">{sales} vente{sales > 1 ? 's' : ''}</span> (<span className="text-red-400 font-bold">{net}</span>)</>;
  } else {
    flowPart = <><span className="font-bold text-primary">{entries} entrée{entries > 1 ? 's' : ''}</span> et <span className="font-bold text-primary">{sales} vente{sales > 1 ? 's' : ''}</span> (<span className="text-secondary font-bold">stable</span>)</>;
  }

  // Rotation sentence
  let rotationPart = null;
  if (rotation != null) {
    let quality = null;
    if (prevRotation != null) {
      const diff = rotation - prevRotation;
      if (diff < -2) quality = <span className="font-bold text-green-400">en nette amélioration</span>;
      else if (diff < 0) quality = <span className="font-bold text-green-400">en amélioration</span>;
      else if (diff === 0) quality = <span className="font-bold text-secondary">stable</span>;
      else if (diff <= 2) quality = <span className="font-bold text-amber-400">en légère hausse</span>;
      else quality = <span className="font-bold text-red-400">en hausse</span>;
    }
    rotationPart = <>Rotation à <span className="font-bold text-cyan-400">{rotation}j</span>{quality && <> — {quality}</>}</>;
  }

  return (
    <div className="rounded-lg p-3" style={{ background: 'rgba(6,182,212,0.04)', border: '1px solid rgba(6,182,212,0.1)' }}>
      <p className="text-xs text-secondary leading-relaxed">
        {flowPart} sur {periodLabel}.{rotationPart && <> {rotationPart}.</>}
      </p>
    </div>
  );
};

/* ── Main Component ──────────────────────────────────────── */
const VisibilityPanel = ({ data, vehicles, settings, interest, onCategoryClick, children }) => {
  const [selectedPeriod, setSelectedPeriod] = useState('7');

  // Stock data: all 3 statuses for total/value, available+reserved for health
  const stockData = useMemo(() => {
    if (!vehicles) return { healthy: [], toWatch: [], dormant: [], avgAge: 0, total: 0, stockValue: 0, statusCounts: { available: 0, in_preparation: 0, reserved: 0 }, healthTotal: 0 };

    const inStock = vehicles.filter(
      v => v.stock_status === 'available' || v.stock_status === 'reserved' || v.stock_status === 'in_preparation'
    );
    const total = inStock.length;
    const stockValue = inStock.reduce((sum, v) => sum + (v.price || 0), 0);
    const byStatus = {
      available: inStock.filter(v => v.stock_status === 'available'),
      in_preparation: inStock.filter(v => v.stock_status === 'in_preparation'),
      reserved: inStock.filter(v => v.stock_status === 'reserved'),
    };
    const statusCounts = {
      available: byStatus.available.length,
      in_preparation: byStatus.in_preparation.length,
      reserved: byStatus.reserved.length,
    };

    // Health is based on available + reserved only
    const healthVehicles = inStock.filter(v => v.stock_status === 'available' || v.stock_status === 'reserved');
    const healthTotal = healthVehicles.length;
    const withDays = healthVehicles.map(v => ({
      ...v,
      daysInStock: getDaysInStock(v.created_at),
    }));

    const healthy = withDays.filter(v => v.daysInStock < 21 && v.images?.length > 0);
    const toWatch = withDays.filter(v => (v.daysInStock >= 21 && v.daysInStock <= 45) || (!v.images?.length && v.daysInStock < 45));
    const dormant = withDays.filter(v => v.daysInStock > 45 && v.images?.length > 0);
    const avgAge = healthTotal > 0
      ? Math.round(withDays.reduce((sum, v) => sum + v.daysInStock, 0) / healthTotal)
      : 0;

    // Median age per category
    const median = (arr) => {
      if (arr.length === 0) return null;
      const sorted = arr.map(v => v.daysInStock).sort((a, b) => a - b);
      const mid = Math.floor(sorted.length / 2);
      return sorted.length % 2 !== 0 ? sorted[mid] : Math.round((sorted[mid - 1] + sorted[mid]) / 2);
    };
    const medianAge = { healthy: median(healthy), toWatch: median(toWatch), dormant: median(dormant) };

    return { healthy, toWatch, dormant, avgAge, medianAge, total, stockValue, statusCounts, byStatus, healthTotal };
  }, [vehicles]);

  if (!data) return null;

  const { scores, platforms } = data;
  const { performance } = scores;
  const periods = performance.periods || {};
  const currentPeriod = periods[selectedPeriod] || {};
  const prev = currentPeriod.prev || {};

  return (
    <div className="space-y-4">
      {/* ── Row 1: 3 cartes côte à côte ──────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

        {/* ── Carte 1: Mon stock ────────────────────────────── */}
        <div className="bg-dark-card rounded-xl border border-dark-border p-4 sm:p-5 space-y-4">
          <h2 className="text-lg font-semibold text-primary flex items-center gap-2">
            <CarFront className="h-5 w-5 text-cyan-500" />
            Stock actuel
          </h2>

          {/* Donut + legend side by side */}
          <div className="flex items-center gap-5">
            <ScoreCircle
              size={150}
              strokeWidth={8}
              segments={[
                { value: stockData.total > 0 ? (stockData.statusCounts.available / stockData.total) * 100 : 0, color: 'rgba(74,222,128,0.45)' },
                { value: stockData.total > 0 ? (stockData.statusCounts.in_preparation / stockData.total) * 100 : 0, color: 'rgba(251,191,36,0.55)' },
                { value: stockData.total > 0 ? (stockData.statusCounts.reserved / stockData.total) * 100 : 0, color: 'rgba(96,165,250,0.55)' },
              ]}
            >
              <div className="text-center">
                <span className="text-3xl font-extrabold text-primary block leading-none">{stockData.total}</span>
                <span className="text-[10px] font-semibold text-secondary">véhicules</span>
                <span className="text-[9px] text-muted block mt-1">valeur</span>
                <span className="text-sm font-bold text-emerald-400 block leading-none">{formatCompact(stockData.stockValue)}</span>
              </div>
            </ScoreCircle>

            {/* Clickable legend — simple count/total */}
            <div className="flex-1 space-y-2.5">
              {[
                { label: 'Disponible', count: stockData.statusCounts.available, dotColor: 'bg-green-400', key: 'available' },
                { label: 'En préparation', count: stockData.statusCounts.in_preparation, dotColor: 'bg-amber-400', key: 'in_preparation' },
                { label: 'Réservé', count: stockData.statusCounts.reserved, dotColor: 'bg-blue-400', key: 'reserved' },
              ].map(s => (
                <button
                  key={s.key}
                  onClick={() => onCategoryClick?.(s.key, stockData.byStatus[s.key])}
                  className="w-full flex items-center gap-2 group hover:bg-dark-hover/50 rounded-lg px-1.5 py-1 -mx-1.5 transition-colors"
                >
                  <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${s.dotColor}`} />
                  <span className="text-xs text-secondary flex-1 text-left">{s.label}</span>
                  <span className="text-xs font-bold text-primary">{s.count}</span>
                  <span className="text-[10px] text-muted">/{stockData.total}</span>
                  <ChevronRight className="h-3 w-3 text-muted opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ── Carte 2: Santé du stock ──────────────────────── */}
        <div className="bg-dark-card rounded-xl border border-dark-border p-4 sm:p-5 space-y-3">
          <h2 className="text-lg font-semibold text-primary flex items-center gap-2">
            <Activity className="h-5 w-5 text-cyan-500" />
            {L.sectionStockHealth}
          </h2>
          <p className="text-[11px] text-muted">{L.stockAvgAge} : <span className="text-secondary font-semibold">{stockData.avgAge}{L.days}</span></p>

          {vehicles && vehicles.length > 0 && (() => {
            const maxCount = Math.max(stockData.healthy.length, stockData.toWatch.length, stockData.dormant.length, 1);
            const categories = [
              { key: 'healthy', label: 'En forme', count: stockData.healthy.length, vehicles: stockData.healthy, median: stockData.medianAge.healthy, color: '#4ade80', colorClass: 'text-green-400', gradient: 'from-green-400/30 to-green-400/60' },
              { key: 'toWatch', label: 'À surveiller', count: stockData.toWatch.length, vehicles: stockData.toWatch, median: stockData.medianAge.toWatch, color: '#fbbf24', colorClass: 'text-amber-400', gradient: 'from-amber-400/30 to-amber-400/60' },
              { key: 'dormant', label: 'Dormant', count: stockData.dormant.length, vehicles: stockData.dormant, median: stockData.medianAge.dormant, color: '#f87171', colorClass: 'text-red-400', gradient: 'from-red-400/30 to-red-400/60' },
            ];
            return (
              <div className="flex items-end justify-center gap-6" style={{ height: 180 }}>
                {categories.map(cat => {
                  const pct = maxCount > 0 ? Math.max((cat.count / maxCount) * 100, cat.count > 0 ? 8 : 3) : 3;
                  return (
                    <button
                      key={cat.key}
                      onClick={() => onCategoryClick?.(cat.key, cat.vehicles)}
                      className="flex flex-col items-center flex-1 h-full justify-end group transition-opacity hover:opacity-80"
                    >
                      <span className={`text-xl font-extrabold ${cat.count > 0 ? 'text-primary' : 'text-muted'} mb-1.5`}>{cat.count}</span>
                      <div className="w-12 rounded-t-lg overflow-hidden transition-all duration-500" style={{ height: `${pct}%` }}>
                        <div className={`w-full h-full bg-gradient-to-t ${cat.gradient}`} />
                      </div>
                      <div className="mt-2.5 text-center">
                        <span className="text-[11px] font-semibold text-secondary block">{cat.label}</span>
                        <span className="text-[10px] text-muted">
                          âge méd. <span className={`font-semibold ${cat.colorClass}`}>{cat.median != null ? `${cat.median}j` : '—'}</span>
                        </span>
                      </div>
                      <ChevronRight className="h-3 w-3 text-muted opacity-0 group-hover:opacity-100 transition-opacity mt-0.5" />
                    </button>
                  );
                })}
              </div>
            );
          })()}
        </div>

        {/* ── Carte 3: Performance ─────────────────────────── */}
        <div className="bg-dark-card rounded-xl border border-dark-border p-4 sm:p-5 space-y-3">
          <h2 className="text-lg font-semibold text-primary flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-cyan-500" />
            {L.performanceCardTitle}
            <InfoTooltip text={L.performanceTooltip} />
          </h2>

          <PeriodSelector selected={selectedPeriod} onChange={setSelectedPeriod} />

          <PerformanceSummary period={{ ...currentPeriod, prev }} periodLabel={`${selectedPeriod}${L.days}`} />

          <div className="grid grid-cols-3 gap-2">
            <div className="bg-dark-hover/60 rounded-lg p-2.5 text-center">
              <p className="text-[9px] font-semibold text-muted uppercase tracking-widest mb-1">Entrées</p>
              <span className="text-lg font-extrabold text-primary">{currentPeriod.entries?.count ?? '—'}</span>
              <div className="flex justify-center mt-0.5">
                <EvolutionBadge current={currentPeriod.entries?.count} previous={prev.entries?.count} />
              </div>
              <p className="text-[10px] text-muted mt-0.5">{formatCompact(currentPeriod.entries?.value)}</p>
            </div>
            <div className="bg-dark-hover/60 rounded-lg p-2.5 text-center">
              <p className="text-[9px] font-semibold text-muted uppercase tracking-widest mb-1">Ventes</p>
              <span className="text-lg font-extrabold text-primary">{currentPeriod.sales?.count ?? '—'}</span>
              <div className="flex justify-center mt-0.5">
                <EvolutionBadge current={currentPeriod.sales?.count} previous={prev.sales?.count} />
              </div>
              <p className="text-[10px] text-muted mt-0.5">{formatCompact(currentPeriod.sales?.value)}</p>
            </div>
            <div className="bg-dark-hover/60 rounded-lg p-2.5 text-center">
              <p className="text-[9px] font-semibold text-muted uppercase tracking-widest mb-1">Rotation</p>
              <div className="flex items-baseline justify-center gap-0.5">
                <span className="text-lg font-extrabold text-primary">
                  {currentPeriod.sales?.avg_days != null ? currentPeriod.sales.avg_days : '—'}
                </span>
                {currentPeriod.sales?.avg_days != null && <span className="text-[10px] text-muted">{L.days}</span>}
              </div>
              <div className="flex justify-center mt-0.5">
                <EvolutionBadge current={currentPeriod.sales?.avg_days} previous={prev.sales?.avg_days} invert />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Row 2: Analyse + Diffusion côte à côte ────── */}
      <div className="grid grid-cols-1 lg:grid-cols-[3fr_2fr] gap-4">
        {children}

        {/* ── Diffusion ──────────────────────────────────── */}
        <div className="bg-dark-card rounded-xl border border-dark-border p-4 sm:p-5 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-primary flex items-center gap-2">
            <Eye className="h-5 w-5 text-cyan-500" />
            {L.diffusionCardTitle}
            <InfoTooltip text={L.diffusionTooltip} />
          </h2>
          <Link
            to="/settings?tab=social"
            className="text-xs text-cyan-400 hover:text-cyan-300 font-medium flex items-center gap-0.5"
          >
            {L.manageLink} <ChevronRight className="h-3 w-3" />
          </Link>
        </div>

        {/* 3 gauge sections — inline on desktop */}
        <div className="grid grid-cols-1 gap-6">
          {/* ── Section 1: Plateformes d'annonces ── */}
          {(() => {
            const activeAdPlatforms = settings?.active_ad_platforms || {};
            const activeMkp = (platforms.marketplace || []).filter(
              p => activeAdPlatforms[p.id] !== false
            );
            const totalVehicles = activeMkp[0]?.total || 0;
            const mkpDenominator = activeMkp.length * totalVehicles;
            const mkpPublished = activeMkp.reduce((sum, p) => sum + p.published, 0);
            const mkpSegments = activeMkp.map(p => ({
              value: mkpDenominator > 0 ? (p.published / mkpDenominator) * 100 : 0,
              color: (PLATFORM_CONFIG[p.id] || {}).hex || '#6b7280',
            }));

            return activeMkp.length > 0 ? (
              <div>
                <p className="text-[10px] font-semibold text-muted uppercase tracking-widest mb-2">{L.semiMarketplace}</p>
                <div className="flex items-start gap-4">
                  <ScoreCircle segments={mkpSegments} />
                  <div className="flex-1 space-y-1 pt-2">
                    {activeMkp.map(p => {
                      const pVehicles = (vehicles || []).filter(v =>
                        (v.published_urls || []).some(u => u.platform === p.id && !u.unpublished)
                      );
                      return (
                        <PlatformRow
                          key={p.id} id={p.id} name={p.name} published={p.published} total={p.total}
                          detail={`${p.published}/${p.total}`}
                          onClick={() => onCategoryClick?.(p.id, pVehicles)}
                        />
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : null;
          })()}

          {/* ── Section 2: Réseaux sociaux ── */}
          {(() => {
            const socialPlatforms = platforms.social || [];
            const socialTotal = socialPlatforms.reduce((s, p) => s + (p.total || 0), 0);
            const socialPublished = socialPlatforms.reduce((s, p) => s + (p.published || 0), 0);
            const socialSegments = socialPlatforms.map(p => ({
              value: socialTotal > 0 ? ((p.published || 0) / socialTotal) * 100 : 0,
              color: (PLATFORM_CONFIG[p.id] || {}).hex || '#6b7280',
            }));

            return socialPlatforms.length > 0 ? (
              <div>
                <p className="text-[10px] font-semibold text-muted uppercase tracking-widest mb-2">{L.semiSocial}</p>
                <div className="flex items-start gap-4">
                  <ScoreCircle segments={socialSegments} />
                  <div className="flex-1 space-y-1 pt-2">
                    {socialPlatforms.map(p => {
                      const pVehicles = (vehicles || []).filter(v =>
                        (v.social_posts || []).some(sp => sp.platform === p.id)
                      );
                      return (
                        <PlatformRow
                          key={p.id} id={p.id} name={p.name} published={p.published} total={p.total}
                          detail={`${p.published}/${p.total}`}
                          onClick={() => onCategoryClick?.(p.id, pVehicles)}
                        />
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : null;
          })()}

          {/* ── Section 3: Vitrine (multi-segment) ── */}
          {platforms.vitrine && (() => {
            const vitrineTotal = platforms.vitrine?.vehicle_count || 0;
            const vitrineEligible = platforms.vitrine?.total_eligible || 1;

            const topCount = interest?.top_vehicles?.length || 0;
            const noCount = interest?.no_interest?.length || 0;

            const vitrineSegments = [
              topCount > 0 && { value: (topCount / vitrineEligible) * 100, color: VITRINE_SEGMENT_COLORS.top.stroke },
              noCount > 0 && { value: (noCount / vitrineEligible) * 100, color: VITRINE_SEGMENT_COLORS.noInterest.stroke },
            ].filter(Boolean);

            return (
              <div>
                <p className="text-[10px] font-semibold text-muted uppercase tracking-widest mb-2">{L.semiVitrine}</p>
                <div className="flex items-start gap-4">
                  <ScoreCircle segments={vitrineSegments} />
                  <div className="flex-1 space-y-1 pt-2">
                    {topCount > 0 && (
                      <StockHealthRow
                        label={L.vitrineTopConsulted}
                        count={topCount}
                        total={vitrineEligible}
                        dotColor={VITRINE_SEGMENT_COLORS.top.dot}
                        barColor={VITRINE_SEGMENT_COLORS.top.bar}
                        onClick={() => onCategoryClick?.('topVehicles', interest.top_vehicles)}
                      />
                    )}
                    {noCount > 0 && (
                      <StockHealthRow
                        label={L.vitrineNoInterest}
                        count={noCount}
                        total={vitrineEligible}
                        dotColor={VITRINE_SEGMENT_COLORS.noInterest.dot}
                        barColor={VITRINE_SEGMENT_COLORS.noInterest.bar}
                        onClick={() => onCategoryClick?.('noInterest', interest.no_interest)}
                      />
                    )}
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      </div>
      </div>
    </div>
  );
};

export default VisibilityPanel;
